import * as THREE from 'three'
import Experience from '../Experience.js'

// Repères du parcours (trous, départs, bunkers) en HTML/CSS : chaque
// repère est un élément du conteneur `.labels`, projeté chaque frame au
// sommet d'une tige 3D plantée dans le terrain. Le style vit dans
// style.css (« Repères du parcours »).
const FLAG_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 21V4"/><path d="M6 4.5c4-2 6.5 2 11 0v8c-4.5 2-7-2-11 0"/></svg>`
const SAND_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10c2.7-2 5.3 2 8 0s5.3-2 8 0"/><path d="M4 15c2.7-2 5.3 2 8 0s5.3-2 8 0"/></svg>`

const TEE_LABELS = {
    white: 'Blanc',
    yellow: 'Jaune',
    blue: 'Bleu',
    red: 'Rouge'
}

const STEMS = {
    hole: { height: 0.1, opacity: 0.85 },
    bunker: { height: 0.045, opacity: 0.6 },
    tee: { height: 0.025, opacity: 0.6 }
}

export default class Labels {
    constructor() {
        this.experience = new Experience()

        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.world = this.experience.world
        this.sizes = this.experience.sizes
        this.container = document.querySelector('.labels')

        this.labels = []
        this.holeLabels = []
        this.bunkerLabels = []
        this.teeLabels = []
        this.labelsByHole = {}

        this.projected = new THREE.Vector3()
        this.selectedTee = undefined

        this.loadLabels()
        this.setInfoLabels()
    }

    loadLabels() {
        this.holes = this.resources.items.holes
        this.bunkers = this.resources.items.bunkers
        this.tees = this.resources.items.tees
        this.info = this.resources.items.info ?? {}
    }

    setInfoLabels() {
        const terrain = this.world.terrain

        const create = (feature, type, element) => {
            const [geoX, geoY] = feature.geometry.coordinates
            const x = -geoX
            const z = -geoY
            const y = terrain.getHeightAt(x, z)
            const hole = feature.properties.hole

            const base = new THREE.Vector3(x, y, z)
            const top = new THREE.Vector3(x, y + STEMS[type].height, z)
            const line = this.createLine(base, top, STEMS[type].opacity)
            line.visible = false
            this.scene.add(line)

            element.style.opacity = '0'
            this.container.appendChild(element)

            const label = {
                type,
                hole,
                element,
                line,
                base,
                top,
                opacity: 0,
                shownOpacity: -1,
                screen: null
            }

            if (!this.labelsByHole[hole]) this.labelsByHole[hole] = []
            this.labelsByHole[hole].push(label)
            this.labels.push(label)
            return label
        }

        this.holeLabels = this.holes.features.map(f => create(f, 'hole', this.createHoleElement(f.properties.hole)))
        this.bunkerLabels = this.bunkers.features.map(f => create(f, 'bunker', this.createBunkerElement()))
        this.teeLabels = this.tees.features.map(f => {
            const label = create(f, 'tee', this.createTeeElement(f.properties.tee))
            label.tee = f.properties.tee
            return label
        })
    }

    //
    // ÉLÉMENTS
    //

    createHoleElement(hole) {
        const info = this.info[hole] ?? {}
        const par = info.par
        const element = document.createElement('div')
        element.className = 'marker marker--hole'
        element.dataset.hole = String(hole)
        // La carte (nom, par, handicap, distance du départ choisi) ne
        // s'affiche que sur le plan, au survol ou au toucher
        element.innerHTML = `
            <div class="marker__body">
                <span class="marker__flag">${FLAG_ICON}</span>
                <span class="marker__num">${hole}</span>
                ${par ? `<span class="marker__par">Par ${par}</span>` : ''}
                <span class="marker__card" aria-hidden="true">
                    <span class="marker__card-name">${info.nom ?? `Trou ${hole}`}</span>
                    <span class="marker__card-meta">Par ${par ?? '–'} · Hcp ${info.handicap ?? '–'}</span>
                    <span class="marker__card-distance" data-tee="white">
                        <span class="marker__card-dot"></span>
                        <span data-distance>${info.white ?? '–'} m</span>
                    </span>
                    <span class="marker__card-go">Voir le trou →</span>
                </span>
            </div>
        `
        element.setAttribute('aria-label', `Trou ${hole}${par ? `, par ${par}` : ''}`)
        return element
    }

    createBunkerElement() {
        const element = document.createElement('div')
        element.className = 'marker marker--bunker'
        element.innerHTML = `<div class="marker__body">${SAND_ICON}</div>`
        element.setAttribute('aria-label', 'Bunker')
        return element
    }

    createTeeElement(tee) {
        const element = document.createElement('div')
        element.className = `marker marker--tee marker--tee-${tee}`
        element.innerHTML = `
            <div class="marker__body">
                <span class="marker__dot"></span>
                <span class="marker__tee-label">Départ ${TEE_LABELS[tee] ?? ''}</span>
            </div>
        `
        return element
    }

    createLine(base, top, opacity) {
        const geometry = new THREE.BufferGeometry().setFromPoints([base, top])
        const material = new THREE.LineBasicMaterial({
            color: 0xfff6e2,
            transparent: true,
            opacity
        })
        const line = new THREE.Line(geometry, material)
        line.userData.baseOpacity = opacity
        return line
    }

    //
    // VISIBILITÉ
    //

    hideAll() {
        for (const label of this.labels) label.opacity = 0
    }

    setHoleTransition(current, next, t) {
        this.hideAll()
        if (current != null) this.applyOpacity(current, 1 - t)
        if (next != null) this.applyOpacity(next, t)
    }

    applyOpacity(hole, value) {
        const list = this.labelsByHole[hole]
        if (!list) return
        for (const label of list) label.opacity = value
    }

    // Plan du parcours : seuls les numéros de trou, tous affichés
    showHolesOnly() {
        for (const label of this.labels) {
            label.opacity = label.type === 'hole' ? 1 : 0
        }
    }

    //
    // PROJECTION
    //

    syncSelectedTee() {
        const selected = this.experience.teeSelection?.selectedColor ?? null
        if (selected === this.selectedTee) return
        this.selectedTee = selected

        for (const label of this.teeLabels) {
            label.element.classList.toggle('is-selected', label.tee === selected)
        }

        // Distance affichée sur la carte du plan : départ choisi (blanc
        // par défaut)
        const tee = selected ?? 'white'
        for (const label of this.holeLabels) {
            const distance = label.element.querySelector('.marker__card-distance')
            if (!distance) continue
            distance.dataset.tee = tee
            distance.querySelector('[data-distance]').textContent = `${this.info[label.hole]?.[tee] ?? '–'} m`
        }
    }

    // Plan : les badges qui se chevauchent sont écartés verticalement (le
    // plus haut à l'écran remonte), pour que chaque numéro reste lisible
    // et cliquable.
    declutterHoles() {
        const minDx = 66
        const minDy = 34
        const visible = this.holeLabels.filter(l => l.screen)
        for (const label of visible) label.lift = 0

        visible.sort((a, b) => b.screen.y - a.screen.y)
        for (let i = 1; i < visible.length; i++) {
            const label = visible[i]
            for (let pass = 0; pass < 3; pass++) {
                let moved = false
                for (let j = 0; j < i; j++) {
                    const other = visible[j]
                    const dx = Math.abs(label.screen.x - other.screen.x)
                    const dy = (other.screen.y - other.lift) - (label.screen.y - label.lift)
                    if (dx < minDx && Math.abs(dy) < minDy) {
                        label.lift += minDy - dy
                        moved = true
                    }
                }
                if (!moved) break
            }
        }
    }

    update() {
        const camera = this.experience.camera.instance
        const width = this.sizes.width
        const height = this.sizes.height

        this.syncSelectedTee()
        const isMap = this.experience.camera.controlMode === 'map'

        for (const label of this.labels) {
            label.screen = null
            const visible = label.opacity > 0.001
            label.line.visible = visible
            if (visible) label.line.material.opacity = label.line.userData.baseOpacity * label.opacity

            let opacity = 0

            if (visible) {
                this.projected.copy(label.top).project(camera)
                const inView = this.projected.z > -1 && this.projected.z < 1
                    && Math.abs(this.projected.x) < 1.2 && Math.abs(this.projected.y) < 1.2

                if (inView) {
                    opacity = label.opacity
                    const x = (this.projected.x * 0.5 + 0.5) * width
                    const y = (-this.projected.y * 0.5 + 0.5) * height
                    label.screen = { x, y }
                    label.element.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`
                    // Les repères proches passent devant les lointains
                    label.element.style.zIndex = String(Math.round((1 - this.projected.z) * 5000) + (label.type === 'hole' ? 10000 : 0))
                }
            }

            if (opacity !== label.shownOpacity) {
                label.shownOpacity = opacity
                label.element.style.opacity = opacity.toFixed(3)
                label.element.classList.toggle('is-visible', opacity > 0.001)
            }
        }

        if (isMap) this.declutterHoles()
        for (const label of this.holeLabels) {
            const lift = isMap ? Math.round(label.lift ?? 0) : 0
            if (lift !== label.shownLift) {
                label.shownLift = lift
                label.element.style.setProperty('--lift', `${lift}px`)
            }
        }
    }

    destroy() {
        for (const label of this.labels) {
            this.scene.remove(label.line)
            label.line.geometry.dispose()
            label.line.material.dispose()
            label.element.remove()
        }

        this.labels = []
        this.holeLabels = []
        this.bunkerLabels = []
        this.teeLabels = []
        this.labelsByHole = {}
    }
}
